/**
 * FxPanel — purely-presentational FX controls extracted from
 * CreatorRecorder.jsx in the iter-41 split. Receives all state + setters
 * as props so the parent retains ownership of the rAF render loop.
 */
import React from "react";
import {
  Mic, MicOff, Sparkles, ScanFace, Wand2, Timer, Image as ImageIcon, Film,
} from "lucide-react";
import AiBgGenerator from "../AiBgGenerator";


function Chip({ active, children, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`chip text-[11px] ${active ? "active" : ""}`}>{children}</button>
  );
}

function FxRow({ label, children }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[9px] uppercase tracking-widest text-[#A1A1AA] font-bold w-20 shrink-0">{label}</span>
      <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">{children}</div>
    </div>
  );
}


export default function FxPanel({
  // Filter / beauty
  COLOR_FILTERS, filter, setFilter,
  beauty, setBeauty,
  // AR
  AR_STICKERS, arSticker, setArSticker,
  // Background
  BG_FX, bgFx, setBgFx, setBgImage, loadSeg,
  // Timer
  TIMERS, timer, setTimer,
  // Mic + clips review
  micOn, setMicOn,
  clips, recState, onReview,
}) {
  return (
    <div className="space-y-3">
      <FxRow label="Color">
        {COLOR_FILTERS.map(f => (
          <Chip key={f.id} active={filter === f.id}
            testid={`fx-color-${f.id}`}
            onClick={() => setFilter(f.id)}>{f.label}</Chip>
        ))}
      </FxRow>

      <FxRow label={<><Sparkles size={11} className="inline mr-1"/>Beauty</>}>
        <input type="range" min="0" max="3" step="1" value={beauty}
          onChange={(e) => setBeauty(parseInt(e.target.value))}
          data-testid="fx-beauty-slider"
          className="flex-1 accent-[#D4FF00]"/>
        <span className="text-[10px] text-[#A1A1AA] w-12 text-right font-mono-display">
          {["off","low","mid","high"][beauty]}
        </span>
      </FxRow>

      <FxRow label={<><ScanFace size={11} className="inline mr-1"/>AR</>}>
        {AR_STICKERS.map(s => (
          <Chip key={s.id} active={arSticker === s.id}
            testid={`fx-ar-${s.id}`}
            onClick={() => setArSticker(s.id)}>
            {s.emoji ? <span className="mr-1">{s.emoji}</span> : null}{s.label}
          </Chip>
        ))}
      </FxRow>

      <FxRow label={<><Wand2 size={11} className="inline mr-1"/>Background</>}>
        {BG_FX.map(b => (
          <Chip key={b.id} active={bgFx === b.id}
            testid={`fx-bg-${b.id}`}
            onClick={() => { setBgFx(b.id); if (b.id !== "off") loadSeg(); }}>{b.label}</Chip>
        ))}
        <label className="chip cursor-pointer" data-testid="fx-bg-upload">
          <ImageIcon size={11} className="inline mr-1"/>Upload
          <input type="file" accept="image/*" className="hidden" onChange={(e) => {
            const f = e.target.files?.[0]; if (!f) return;
            if (f.size > 2_500_000) { alert("Max 2.5MB."); return; }
            const r = new FileReader();
            r.onload = () => { setBgImage(r.result); setBgFx("custom"); loadSeg(); };
            r.readAsDataURL(f);
          }}/>
        </label>
      </FxRow>

      <div className="pt-2 border-t border-[#3D2845]" data-testid="rec-ai-bg-section">
        <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-2">
          <Sparkles size={10} className="inline mr-1 text-[#D4FF00]"/>AI background
        </div>
        <AiBgGenerator
          onPick={(url) => { setBgImage(url); setBgFx("custom"); loadSeg(); }}
        />
      </div>

      <FxRow label={<><Timer size={11} className="inline mr-1"/>Timer</>}>
        {TIMERS.map(t => (
          <Chip key={t} active={timer === t}
            testid={`fx-timer-${t}`}
            onClick={() => setTimer(t)}>{t === 0 ? "Off" : `${t}s`}</Chip>
        ))}
      </FxRow>

      <div className="flex flex-wrap items-center gap-2 justify-center pt-2 border-t border-[#3D2845]">
        <button onClick={() => setMicOn(!micOn)} className="btn-secondary text-xs" data-testid="rec-mic-toggle">
          {micOn ? <Mic size={12} className="inline mr-1"/> : <MicOff size={12} className="inline mr-1"/>}
          {micOn ? "Mic on" : "Mic off"}
        </button>
        {clips.length > 0 && recState === "idle" && (
          <button onClick={onReview} data-testid="rec-review-btn" className="btn-secondary text-xs">
            <Film size={12} className="inline mr-1"/>Review {clips.length} clip{clips.length !== 1 ? "s" : ""}
          </button>
        )}
      </div>
    </div>
  );
}
